import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { AmenitiesState } from "@/lib/plan-editor";

/**
 * Phase 25 — Plan Editor Amenities section.
 *
 * Dumb controlled component. Ports the Amenities tab from the retired
 * popup editor (deleted in Plan 25-06).
 *
 * Renders 2 freeform Textareas (dedicated + common) with a live preview of the
 * comma-split chips beneath. Phase 26 will replace these with a managed master
 * list — until then operators enter freeform comma-separated strings.
 *
 * Covers: ACCORD25-01 field surface (Amenities).
 */

export interface AmenitiesSectionProps {
  value: AmenitiesState;
  onChange: (next: AmenitiesState) => void;
  /** Plan 25-04 — Save & Continue button click handler. */
  onSave: () => void | Promise<void>;
  /** Plan 25-04 — true while any section save is in flight. */
  saving: boolean;
  /** 2026-06-09 — locks all inputs when true */
  disabled?: boolean;
  /** 2026-06-09 — Edit button handler */
  onEdit?: () => void;
}

function chips(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function AmenitiesSection({
  value,
  onChange,
  onSave,
  saving,
  disabled = false,
  onEdit,
}: AmenitiesSectionProps) {
  function patch(next: Partial<AmenitiesState>) {
    onChange({ ...value, ...next });
  }

  const dedicatedChips = chips(value.dedicatedAmenitiesText);
  const commonChips = chips(value.commonAmenitiesText);
  const hasAny = dedicatedChips.length > 0 || commonChips.length > 0;

  return (
    <div className="space-y-4 p-1" data-testid="plan-editor-amenities-section">
    <fieldset disabled={disabled} className="space-y-4 disabled:opacity-70">
      <div className="space-y-1.5">
        <Label>Dedicated Amenities (comma-separated)</Label>
        <Textarea
          value={value.dedicatedAmenitiesText}
          onChange={(e) => patch({ dedicatedAmenitiesText: e.target.value })}
          placeholder="Comma-separated, e.g. Standing Desk, Ergonomic Chair, Locker"
          rows={2}
        />
        <p className="text-[10px] text-muted-foreground">
          Items dedicated to members on this plan (e.g., a reserved locker).
        </p>
      </div>

      <div className="space-y-1.5">
        <Label>Common Amenities (comma-separated)</Label>
        <Textarea
          value={value.commonAmenitiesText}
          onChange={(e) => patch({ commonAmenitiesText: e.target.value })}
          placeholder="Comma-separated, e.g. WiFi, AC, Power backup"
          rows={2}
        />
        <p className="text-[10px] text-muted-foreground">
          Shared amenities accessible to all members on this plan.
        </p>
      </div>

      <p className="text-[10px] text-muted-foreground">
        Phase 26 will replace these with a managed master list.
      </p>

      {hasAny && (
        <div className="rounded-md border bg-muted/30 p-3 space-y-2">
          <p className="text-[10px] font-medium text-muted-foreground">Preview</p>
          {dedicatedChips.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {dedicatedChips.map((a) => (
                <Badge key={a} variant="secondary" className="text-xs">
                  {a}
                </Badge>
              ))}
            </div>
          )}
          {commonChips.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {commonChips.map((a) => (
                <Badge key={a} variant="outline" className="text-xs">
                  {a}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      </fieldset>
      <div className="flex justify-end pt-2">
        {disabled ? (
          <Button
            type="button"
            variant="outline"
            onClick={onEdit}
            data-testid="plan-editor-amenities-edit"
          >
            Edit
          </Button>
        ) : (
          <Button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="plan-editor-amenities-save"
        >
          {saving ? "Saving…" : "Save & Continue"}
        </Button>
        )}
      </div>
    </div>
  );
}
